import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { ArrowRight, Mail, RefreshCw } from 'lucide-react'
import { LoginFormImport } from './Import'
import { isStudentRole } from '../../../utils/isStudent'
import { getConfig } from '../../../utils'
const { RoutesMap, Form, InputGroup, Button, Card, logo, useFormik, LoginSchema, useNavigate, useDispatch, toast, KeyRound, UserRound, Eye, EyeOff, ButtonLoader, SessionManager, signIn, sidebar } = LoginFormImport

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/
const FORGOT_STEPS = [
  { key: 'email', label: 'Email' },
  { key: 'otp', label: 'OTP' },
  { key: 'reset', label: 'Password' },
]

async function postForgot(path, data) {
  try {
    const res = await axios({
      method: 'post',
      url: getConfig.API_URL + path,
      data,
      headers: { 'Content-Type': 'application/json' },
    })
    return res
  } catch (error) {
    return error.response
  }
}

function forgotMessage(res, fallback) {
  return res?.data?.message || fallback
}

function forgotOk(res) {
  return res?.status >= 200 && res?.status < 300 && res?.data?.success !== false
}

export function ForgotPasswordPanel({ variant = 'staff', onCancel }) {
  const [step, setStep] = useState('email')
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [resetToken, setResetToken] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const hint = step === 'email'
    ? 'Enter your email and we will send a one-time code.'
    : step === 'otp'
      ? `Enter the code sent to ${email.trim()}`
      : 'Choose a new password for your account.'

  const actionLabel = step === 'email' ? 'Send OTP' : step === 'otp' ? 'Verify OTP' : 'Reset Password'

  const fail = (res) => {
    toast.error(forgotMessage(res, 'Something went wrong!!..'), { position: 'bottom-right' })
  }

  const sendOtp = async (resend = false) => {
    const value = email.trim()
    if (!EMAIL_RE.test(value)) {
      setError('Enter a valid email')
      return
    }
    setError('')
    setBusy(true)
    const res = await postForgot('forgot_password/send_otp', { email: value })
    setBusy(false)
    if (forgotOk(res)) {
      if (resend) setOtp('')
      toast.success(forgotMessage(res, 'OTP sent successfully to your email'), { position: 'bottom-right' })
      setStep('otp')
      return
    }
    fail(res)
  }

  const verifyOtp = async () => {
    const code = otp.trim()
    if (!/^\d{4,8}$/.test(code)) {
      setError('Enter the OTP')
      return
    }
    setError('')
    setBusy(true)
    const res = await postForgot('forgot_password/verify_otp', { email: email.trim(), otp: code })
    setBusy(false)
    const token = res?.data?.data?.reset_token
    if (forgotOk(res) && token) {
      setResetToken(token)
      setPassword('')
      setConfirmPassword('')
      toast.success(forgotMessage(res, 'OTP verified successfully'), { position: 'bottom-right' })
      setStep('reset')
      return
    }
    fail(res)
  }

  const resetPassword = async () => {
    if (!password || password.length < 6) {
      setError('Enter a password of at least 6 characters')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    setError('')
    setBusy(true)
    const res = await postForgot('forgot_password/reset_password', {
      reset_token: resetToken,
      new_password: password,
      confirm_password: confirmPassword,
    })
    setBusy(false)
    if (forgotOk(res)) {
      toast.success(forgotMessage(res, 'Password reset successfully. You can now login with your new password.'), { position: 'bottom-right' })
      onCancel()
      return
    }
    fail(res)
  }

  const submit = () => {
    if (busy) return
    if (step === 'email') sendOtp(false)
    else if (step === 'otp') verifyOtp()
    else resetPassword()
  }

  const onEnter = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      submit()
    }
  }

  const steps = (
    <div className={variant === 'student' ? 'stu-login-steps' : 'login-forgot-steps'}>
      {FORGOT_STEPS.map((item, index) => {
        const current = FORGOT_STEPS.findIndex((row) => row.key === step)
        const cls = index === current ? 'is-active' : index < current ? 'is-done' : ''
        return <span key={item.key} className={cls}>{item.label}</span>
      })}
    </div>
  )

  if (variant === 'student') {
    return (
      <>
        <h3>Reset Password</h3>
        <p className="stu-login-sub">{hint}</p>
        {steps}

        {step === 'email' && (
          <div className="stu-login-group">
            <label htmlFor="stu-forgot-email">Email</label>
            <div className={`stu-login-field ${error ? 'invalid' : ''}`}>
              <Mail size={18} />
              <input
                id="stu-forgot-email"
                type="email"
                placeholder="Enter Your Email"
                autoComplete="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError('') }}
                onKeyDown={onEnter}
              />
            </div>
            {error ? <span className="field_error">{error}</span> : null}
          </div>
        )}

        {step === 'otp' && (
          <div className="stu-login-group">
            <label htmlFor="stu-forgot-otp">OTP</label>
            <div className={`stu-login-field ${error ? 'invalid' : ''}`}>
              <KeyRound size={18} />
              <input
                id="stu-forgot-otp"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="Enter OTP"
                maxLength={8}
                value={otp}
                onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '')); setError('') }}
                onKeyDown={onEnter}
              />
            </div>
            {error ? <span className="field_error">{error}</span> : null}
            <div className="stu-login-forgot-row">
              <button type="button" className="stu-login-forgot-link" onClick={() => { setError(''); setStep('email') }}>Change email</button>
              <button type="button" className="stu-login-forgot-link" disabled={busy} onClick={() => sendOtp(true)}>Resend OTP</button>
            </div>
          </div>
        )}

        {step === 'reset' && (
          <>
            <div className="stu-login-group">
              <label htmlFor="stu-forgot-password">New Password</label>
              <div className={`stu-login-field ${error ? 'invalid' : ''}`}>
                <KeyRound size={18} />
                <input
                  id="stu-forgot-password"
                  type={showNew ? 'text' : 'password'}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError('') }}
                  onKeyDown={onEnter}
                />
                <button type="button" className="stu-login-eye" onClick={() => setShowNew(!showNew)} aria-label="Toggle password">
                  {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div className="stu-login-group">
              <label htmlFor="stu-forgot-confirm">Confirm Password</label>
              <div className={`stu-login-field ${error ? 'invalid' : ''}`}>
                <KeyRound size={18} />
                <input
                  id="stu-forgot-confirm"
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError('') }}
                  onKeyDown={onEnter}
                />
                <button type="button" className="stu-login-eye" onClick={() => setShowConfirm(!showConfirm)} aria-label="Toggle confirm password">
                  {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {error ? <span className="field_error">{error}</span> : null}
            </div>
          </>
        )}

        <button type="button" className="stu-login-btn" disabled={busy} onClick={submit}>
          {busy ? <ButtonLoader /> : <>{actionLabel} <ArrowRight size={18} /></>}
        </button>
        <div className="stu-login-alt">
          <button type="button" className="stu-login-forgot-link" onClick={onCancel}>Back to login</button>
        </div>
      </>
    )
  }

  return (
    <div className="login-forgot">
      <p className="login-forgot-hint">{hint}</p>
      {steps}

      {step === 'email' && (
        <div className="mb-2">
          <Form.Label className="fw-medium mb-1" style={{ color: '#001529' }}>Email</Form.Label>
          <InputGroup size="sm">
            <InputGroup.Text><Mail size={18} /></InputGroup.Text>
            <Form.Control
              className="login-input"
              type="email"
              placeholder="Enter Your Email"
              autoComplete="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError('') }}
              onKeyDown={onEnter}
            />
          </InputGroup>
          {error ? <span className="field_error">{error}</span> : null}
        </div>
      )}

      {step === 'otp' && (
        <div className="mb-2">
          <Form.Label className="fw-medium mb-1" style={{ color: '#001529' }}>OTP</Form.Label>
          <InputGroup size="sm">
            <InputGroup.Text><KeyRound size={18} /></InputGroup.Text>
            <Form.Control
              className="login-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="Enter OTP"
              maxLength={8}
              value={otp}
              onChange={(e) => { setOtp(e.target.value.replace(/\D/g, '')); setError('') }}
              onKeyDown={onEnter}
            />
          </InputGroup>
          {error ? <span className="field_error">{error}</span> : null}
          <div className="login-forgot-row">
            <button type="button" className="login-forgot-link" onClick={() => { setError(''); setStep('email') }}>Change email</button>
            <button type="button" className="login-forgot-link" disabled={busy} onClick={() => sendOtp(true)}>Resend OTP</button>
          </div>
        </div>
      )}

      {step === 'reset' && (
        <>
          <div className="mb-2">
            <Form.Label className="fw-medium mb-1" style={{ color: '#001529' }}>New Password</Form.Label>
            <InputGroup size="sm">
              <InputGroup.Text><KeyRound size={18} /></InputGroup.Text>
              <Form.Control
                type={showNew ? 'text' : 'password'}
                className="login-input"
                placeholder="Enter new password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError('') }}
                onKeyDown={onEnter}
              />
              <InputGroup.Text onClick={() => setShowNew(!showNew)} style={{ cursor: 'pointer' }}>
                {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
              </InputGroup.Text>
            </InputGroup>
          </div>
          <div className="mb-2">
            <Form.Label className="fw-medium mb-1" style={{ color: '#001529' }}>Confirm Password</Form.Label>
            <InputGroup size="sm">
              <InputGroup.Text><KeyRound size={18} /></InputGroup.Text>
              <Form.Control
                type={showConfirm ? 'text' : 'password'}
                className="login-input"
                placeholder="Confirm new password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError('') }}
                onKeyDown={onEnter}
              />
              <InputGroup.Text onClick={() => setShowConfirm(!showConfirm)} style={{ cursor: 'pointer' }}>
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </InputGroup.Text>
            </InputGroup>
            {error ? <span className="field_error">{error}</span> : null}
          </div>
        </>
      )}

      <div className="login-forgot-actions">
        <button type="button" className="login-forgot-link" onClick={onCancel}>Back to login</button>
        <Button type="button" size="sm" className="login_save_btn" disabled={busy} onClick={submit}>
          {busy ? <ButtonLoader /> : actionLabel}
        </Button>
      </div>
    </div>
  )
}

const CAPTCHA_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'

function makeCaptcha(length = 5) {
  let code = ''
  for (let i = 0; i < length; i += 1) {
    code += CAPTCHA_CHARS[Math.floor(Math.random() * CAPTCHA_CHARS.length)]
  }
  return code
}

function drawCaptcha(canvas, code) {
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  const { width, height } = canvas
  ctx.clearRect(0, 0, width, height)
  ctx.fillStyle = '#f4f7fb'
  ctx.fillRect(0, 0, width, height)

  for (let i = 0; i < 18; i += 1) {
    ctx.fillStyle = `rgba(80, 110, 228, ${0.15 + Math.random() * 0.35})`
    ctx.beginPath()
    ctx.arc(Math.random() * width, Math.random() * height, 1.2, 0, Math.PI * 2)
    ctx.fill()
  }

  for (let i = 0; i < 4; i += 1) {
    ctx.strokeStyle = i % 2 ? 'rgba(227, 28, 35, 0.35)' : 'rgba(80, 110, 228, 0.35)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(Math.random() * width, Math.random() * height)
    ctx.lineTo(Math.random() * width, Math.random() * height)
    ctx.stroke()
  }

  ctx.textBaseline = 'middle'
  ctx.textAlign = 'center'
  ctx.font = '700 26px Arial'
  const step = width / (code.length + 1)
  ;[...code].forEach((char, index) => {
    ctx.save()
    ctx.translate(step * (index + 1), height / 2 + (Math.random() * 6 - 3))
    ctx.rotate((Math.random() - 0.5) * 0.55)
    ctx.fillStyle = index % 2 ? '#001529' : '#506EE4'
    ctx.fillText(char, 0, 0)
    ctx.restore()
  })
}

export default function UserLoginForm(props) {

  const { setTogggle } = props

  const [spinner, setSpinner] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [captcha, setCaptcha] = useState(makeCaptcha)
  const [captchaInput, setCaptchaInput] = useState('')
  const [captchaError, setCaptchaError] = useState('')
  const [showForgot, setShowForgot] = useState(false)
  const captchaRef = useRef(null)

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const resetCaptcha = () => {
    setCaptcha(makeCaptcha())
    setCaptchaInput('')
  }

  useEffect(() => {
    if (captchaRef.current) drawCaptcha(captchaRef.current, captcha)
  }, [captcha, showForgot])

  const initialValues = {
    phone: '',
    password: '',
  };

  const { values, errors, touched, handleBlur, handleChange, handleSubmit } = useFormik({
    initialValues: initialValues,
    validationSchema: LoginSchema,

    onSubmit: (values, action) => {
      if (captchaInput.trim() !== captcha) {
        setCaptchaError('Enter the captcha correctly')
        resetCaptcha()
        return
      }
      setCaptchaError('')
      setSpinner(true)
      dispatch(signIn(values)).then((res) => signIn_user(res.payload, action))
    },
  })

  const signIn_user = (res, action) => {

    if (res?.status === 200) {

      // Students sign in from the student login page only
      if (isStudentRole(res?.data?.data?.user)) {
        setSpinner(false)
        toast.error("Students cannot login here. Please use the student login.", { position: "bottom-right" })
        return
      }

      SessionManager.setCookie('user_token', `Bearer ${res?.data?.data?.token}`, 3)
      SessionManager.setCookie('user', res?.data?.data && JSON.stringify(res?.data?.data?.user), 3)

      toast.success("SignIn Successfully!!..", { position: "bottom-right" })

      const loggedInUser = res?.data?.data?.user
      if (['pending', 'hold'].includes(String(loggedInUser?.payment_status || '').toLowerCase())) {
        navigate(RoutesMap.PAYMENT.path)
        setSpinner(false)
        return
      }

      dispatch(sidebar({ navigate })).then((res1) => sidebar_res(res1.payload))

    } else if (res?.status === 400) {
      setSpinner(false)
      toast.error(res?.data?.message, { position: "bottom-right" })
    } else if (res?.status === 500) {
      setSpinner(false)
      toast.error("Server error!!..", { position: "bottom-right" })
    } else {
      setSpinner(false)
      toast.error("Something went wrong!!..", { position: "bottom-right" })
    }
  }

  const sidebar_res = (res) => {
    if (res?.status === 200) {
      const permission = res?.data?.data?.attributes?.map(val => JSON.parse(val)).flat()
      SessionManager.setSession('permission', JSON.stringify(permission))
      navigate(RoutesMap.DASHBOARD.path)
      setSpinner(false)
    } else if (res?.status === 400) {
      setSpinner(false)
      toast.error(res?.data?.message, { position: "bottom-right" })
    } else if (res?.status === 500) {
      setSpinner(false)
      toast.error("Server error!!..", { position: "bottom-right" })
    } else {
      setSpinner(false)
      toast.error("Something went wrong!!..", { position: "bottom-right" })
    }
  }

  return (
    <>
      <Form key={showForgot ? 'forgot' : 'login'} onSubmit={(e) => { e.preventDefault(); if (!showForgot) handleSubmit(); }} className="h-100 login-from-right">
        <Card className="h-100 rounded-start-5 rounded-end-0" style={{ backgroundColor: '#F8FAFC' }}>
          <Card.Header className="p-3 text-end" style={{ backgroundColor: 'unset' }}>
            {/* <span className='fs-6 text-muted'>Don't have an account?
              <u className='fw-medium teal' style={{ cursor: 'pointer' }} onClick={() => setTogggle(true)}> Sign Up</u>
            </span> */}
          </Card.Header>
          <Card.Body className='px-5 py-5 d-flex align-items-center'>
            <div className='flex-fill'>
              <img src={logo} alt="logo" className='img-fluid' style={{ height: '120px' }} />
              <div className='mt-3'>
                <h3 className='teal'>{showForgot ? 'Reset Password' : 'Sign In to Continue'}</h3>
                {!showForgot && <p>Let's get started !!...</p>}
              </div>

              {showForgot ? (
                <ForgotPasswordPanel variant="staff" onCancel={() => setShowForgot(false)} />
              ) : (
              <>
              <div className='mb-2'>
                <Form.Label className='fw-medium mb-1' style={{ color: '#001529' }}>{'Email'}</Form.Label>
                <InputGroup className="" size="sm">
                  <InputGroup.Text id="phone"><UserRound size={18} /></InputGroup.Text>
                  <Form.Control
                    className='login-input'
                    placeholder="Enter Your Email"
                    aria-label="Phone"
                    aria-describedby="phone"
                    name='phone'
                    value={values.phone || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </InputGroup>
                {errors.phone && touched.phone ? (<span className='field_error'>{errors.phone}</span>) : null}
              </div>

              <div className='mb-2'>
                <Form.Label className='fw-medium mb-1' style={{ color: '#001529' }}>{'Password'}</Form.Label>
                <InputGroup className="" size="sm">
                  <InputGroup.Text id="password"><KeyRound size={18} /></InputGroup.Text>
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    className='login-input'
                    placeholder="Enter Your Password"
                    aria-label="Password"
                    aria-describedby="password"
                    name='password'
                    value={values.password || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                  <InputGroup.Text id="password" onClick={() => setShowPassword(!showPassword)} style={{ cursor: 'pointer' }}>
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </InputGroup.Text>
                </InputGroup>
                {errors.password && touched.password ? (<span className='field_error'>{errors.password}</span>) : null}
              </div>

              <div className='mb-2'>
                <Form.Label className='fw-medium mb-1' style={{ color: '#001529' }}>{'Captcha'}</Form.Label>
                <div className="login-captcha">
                  <canvas ref={captchaRef} width={160} height={48} aria-label="Captcha image" />
                  <button type="button" className="login-captcha-refresh" onClick={resetCaptcha} aria-label="Refresh captcha">
                    <RefreshCw size={16} />
                  </button>
                  <Form.Control
                    className='login-input'
                    placeholder="Enter captcha"
                    aria-label="Captcha"
                    name='captcha'
                    autoComplete="off"
                    value={captchaInput}
                    onChange={(e) => { setCaptchaInput(e.target.value); setCaptchaError('') }}
                  />
                </div>
                {captchaError ? (<span className='field_error'>{captchaError}</span>) : null}
                <div className="login-forgot-row">
                  <button type="button" className="login-forgot-link" onClick={() => setShowForgot(true)}>
                    Forgot password?
                  </button>
                </div>
              </div>
              </>
              )}
            </div>
          </Card.Body>
          <Card.Footer className="text-muted d-flex justify-content-between align-items-center" style={{ backgroundColor: 'unset' }}>
            <span style={{ fontSize: '13px' }} className=''>By continuing, you agree to the <u className='teal' style={{ cursor: 'pointer' }}>terms of service</u> and <u className='teal' style={{ cursor: 'pointer' }}>privacy policy</u>.</span>
            {!showForgot && (
              <Button type='submit' size='sm' className='login_save_btn' disabled={spinner}>
                {spinner ?
                  <ButtonLoader />
                  :
                  'Login'
                }
              </Button>
            )}
          </Card.Footer>
        </Card>
      </Form>
    </>
  )
}
